/**
 * Kit tray entries — what the "In your kit:" strip actually prints.
 *
 * The conversation's `confirmedElements` are short generic TOPICS ("hiking
 * backpack 25L"), which is right for the elements the shopper still has to
 * choose. But a pre-buy seeded conversation opens on a product that is already
 * DECIDED: we know its exact title and its exact image. Printing the topic
 * there — and resolving a thumbnail by searching for it — shows a different
 * product than the one being bought (seen live: a Gregory Maya 25 seed rendered
 * as "hiking backpack 25L" over an Osprey Tempest 20 photo).
 *
 * So the seed anchors slot 0 with its own facts, exactly like the built kit
 * page does (`applyKitElementDedup` keeps element 0; stream-create-kit pins its
 * projection), and the topic that stands for it is folded away so the same
 * thing is never listed twice.
 */

export interface KitTrayEntry {
  /** Stable React key, and the thumbnail-cache key for resolved entries. */
  key: string;
  /** The text the tray prints. */
  label: string;
  /** Known up front — pinned entries never resolve a thumbnail by search. */
  image?: string;
  /** The pre-buy seed: its title and image are facts, not guesses. */
  pinned: boolean;
}

export interface KitTraySeed {
  title: string;
  image?: string;
  /** De-slugged category of the page the product sits on — the slot-0 anchor. */
  categoryQuery?: string;
  /**
   * Topics the AI confirmed on its opening turn, before the shopper answered
   * anything. The only product confirmed at that point is the one being bought,
   * so these name the seed even when they share too few words with its title
   * or page ("4K projector" for an Epson Home Cinema 2350 on a home-theater
   * projector page).
   */
  aliases?: readonly string[];
}

/** Accent-fold, lowercase, cheap plural fold — mirrors the kit dedup tokenizer. */
const trayTokens = (s: string): Set<string> =>
  new Set(
    s
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter(Boolean)
      .map((t) => (t.length >= 4 && t.endsWith('s') ? t.slice(0, -1) : t)),
  );

/** One topic however it is cased, accented or pluralized. */
const topicKey = (s: string): string => [...trayTokens(s)].join(' ');

/** Shared-token containment: |A∩B| / min(|A|,|B|) — 1.0 when one is a subset. */
const containment = (a: Set<string>, b: Set<string>): number => {
  if (a.size === 0 || b.size === 0) return 0;
  let shared = 0;
  for (const t of a) if (b.has(t)) shared += 1;
  return shared / Math.min(a.size, b.size);
};

/** Same bar as the server-side near-duplicate guard, for the same reason. */
export const KIT_SEED_ANCHOR_THRESHOLD = 0.8;

/**
 * Build the tray's display list: the pre-buy seed first (real title, real
 * image), then the topics the shopper confirmed, minus the ONE topic that
 * stands for the seed itself.
 */
export const buildKitTrayEntries = (
  kitElements: readonly string[],
  seedProduct?: KitTraySeed | null,
): KitTrayEntry[] => {
  const elements = kitElements.filter((e) => typeof e === 'string' && e.trim().length > 0);
  const seedTitle = seedProduct?.title?.trim();
  if (!seedTitle) return elements.map((label) => ({ key: label, label, pinned: false }));

  // Title AND category: the topic may echo either ("hiking backpack 25L" for a
  // /hiking-backpacks-25l page, "Gregory Maya 25" when they named the model).
  const seedTokens = trayTokens(`${seedProduct?.categoryQuery ?? ''} ${seedTitle}`);
  const aliasKeys = new Set((seedProduct?.aliases ?? []).map(topicKey).filter(Boolean));
  const namesSeed = (label: string) =>
    aliasKeys.has(topicKey(label)) || containment(trayTokens(label), seedTokens) >= KIT_SEED_ANCHOR_THRESHOLD;
  // At most one topic folds away — the seed fills exactly one slot.
  let anchorFolded = false;
  const rest = elements.filter((label) => {
    if (anchorFolded || !namesSeed(label)) return true;
    anchorFolded = true;
    return false;
  });

  return [
    { key: `seed:${seedTitle}`, label: seedTitle, image: seedProduct?.image, pinned: true },
    ...rest.map((label) => ({ key: label, label, pinned: false })),
  ];
};
